<script setup lang="ts">
import { computed, watch, useTemplateRef, ref } from "vue";
import { UiCard, UiFormBuilder, UiText, type PartnerBadge } from "@shopexperts/shared";
import { freelancerDetailsSchema, agencyDetailsSchema, type DetailsFormSchema } from "./schemas/detailsForm";
import { z } from "zod";

import type { BuilderRowConfig } from "@shopexperts/shared";
import ListingEditorPage from "./ListingEditorPage.vue";
import { countriesShopify, languagesEn, timezonesEn, useApi } from "@shopexperts/shared";
import { useProfileDetails } from "@/pages/expert/listing/composables/useProfileDetails";
import { shopifyRoleOptions, agencyRepresentativeRoleOptions } from '@/utils/roles';
import { useMediaQuery } from '@vueuse/core';
import { useListingDetailsStore } from "./composables/useListingDetailsStore";
import { useMediaUploadIntegration } from "./composables/useMediaUploadIntegration";
import PartnerBadgeList from "@/components/common/PartnerBadgeList.vue";
import { useGeoRegions } from "./composables/useGeoRegions";
import { useQuery } from "@tanstack/vue-query";
import CityCombobox from "@/app/components/location/CityCombobox.vue";

const TIMEZONES_OPTIONS = timezonesEn.map(timezone => ({ label: `${timezone.iana.replace(/_/g, ' ')} (${timezone.offset})`, value: timezone.iana }));
const COUNTRIES_OPTIONS = countriesShopify.map(country => ({ label: country.name, value: country.iso2 }));
const LANGUAGES = languagesEn.map(language => ({ label: language.name, value: language.code }));

const ROLES = shopifyRoleOptions;
const PARTNER_TIER_OPTIONS = [
    { label: 'None', value: 'none' },
    { label: 'Select', value: 'select' },
    { label: 'Plus', value: 'plus' },
    { label: 'Premier', value: 'premier' },
    { label: 'Platinum', value: 'platinum' },
];
const props = defineProps<{
    locked?: boolean;
}>();

const onActionClick = () => {
    console.log('action click');
}

const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE;

const { profile, isLoadingProfile, badges, hasActiveProSubscription, subcategoryLimit, subcategoryLimitIsOverride } = useProfileDetails();
const { uploadFn, deleteFn } = useMediaUploadIntegration();
const { options: geoRegionOptions } = useGeoRegions();
const api = useApi();
const { data: categoriesWithSubcategories } = useQuery({
    queryKey: ["service-categories", "with-subs"],
    queryFn: () => api.categories.getCategoriesWithSubcategories(),
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
});
const subcategoryHint = computed(() =>
    !hasActiveProSubscription.value && !subcategoryLimitIsOverride.value
        ? `Select up to ${subcategoryLimit.value} subcategories. Upgrade to Pro for up to 15.`
        : `Select up to ${subcategoryLimit.value} subcategories.`
);
const subcategoryOptions = computed(() =>
    (categoriesWithSubcategories.value || []).flatMap((category) =>
        category.subcategories.map((subcategory) => ({
            label: `${category.name}: ${subcategory.name}`,
            value: String(subcategory.id),
        }))
    )
);
const subcategoryOptionGroups = computed(() =>
    (categoriesWithSubcategories.value || []).map((category) => ({
        id: `category-${category.id}`,
        label: category.name,
        items: category.subcategories.map((subcategory) => ({
            label: subcategory.name,
            value: String(subcategory.id),
        })),
    }))
);

const getAppTierInitialValue = (rawField: unknown): boolean | undefined => {
    if (rawField === 'built_for_shopify') {
        return true;
    }
    if (rawField === null) {
        return false;
    }
    if (rawField && typeof rawField === 'object' && 'value' in rawField) {
        return (rawField as { value?: string | null }).value === 'built_for_shopify';
    }
    return undefined;
};

const getInitialValues = (): DetailsFormSchema | undefined => {
    const p = profile.value;
    if (!p) return;
    if (p.expert_type === 'agency') {
        if (!('agency_name' in p)) return;
        
        const avatarItem = p.profile_picture_media 
            ? { url: p.profile_picture || '', media_id: p.profile_picture_media.id, isTemporary: false }
            : p.profile_picture 
                ? { url: p.profile_picture }
                : undefined;
        
        const representativeItem = p.representative_picture_media
            ? { url: p.representative_picture || '', media_id: p.representative_picture_media.id, isTemporary: false }
            : p.representative_picture
                ? { url: p.representative_picture }
                : undefined;
        
        return {
            expertType: 'agency' as const,
            agencyName: p.agency_name || '',
            agencyType: p.agency_type ?? null,
            agencyTypeFields: p.agency_type_fields,
            country: p.country || '',
            cityId: p.city ? String(p.city.id) : '',
            timezone: p.timezone || '',
            startingPrice: p.starting_price ?? undefined,
            businessAddress: p.address?.address || '',
            shortBio: p.bio || undefined,
            website: p.social_links?.website || undefined,
            phone: p.phone_no || '',
            geoRegions: p.geo_regions || [],
            languages: p.languages || [],
            subcategories: p.subcategories?.map((subcategory) => String(subcategory.id)) || [],
            avatar: avatarItem,
            linkedin: p.social_links?.linkedin_url || undefined,
            github: p.social_links?.github_url || undefined,
            x: p.social_links?.x_url || undefined,
            fiverr: p.social_links?.fiverr_url || undefined,
            upwork: p.social_links?.upwork_url || undefined,
            toptal: p.social_links?.toptal_url || undefined,
            storetasker: p.social_links?.storetasker_url || undefined,
            otherListing: p.social_links?.other_listing_url || undefined,
            representativePicture: representativeItem,
            representativeName: p.representative_name || undefined,
            representativeRole: p.representative_role || undefined,
            badgeIds: p.badges?.map(badge => badge.id) || [],
            partnerTier: p.agency_type === 'app_service_provider' ? undefined : (p.partner_tier || undefined),
            appTier: getAppTierInitialValue(p.agency_type_fields?.app_tier),
            shopifyDirectoryLink: p.social_links?.shopify_directory_link || undefined,
            bookingCallUrl: p.social_links?.booking_call_url || undefined,
            googleMerchant: p.social_links?.google_merchant || undefined,
        };
    } else {
        if (!('first_name' in p)) return;
        
        const avatarItem = p.profile_picture_media 
            ? { url: p.profile_picture || '', media_id: p.profile_picture_media.id, isTemporary: false }
            : p.profile_picture 
                ? { url: p.profile_picture }
                : undefined;
        
        return {
            expertType: 'freelancer' as const,
            firstName: p.first_name || '',
            lastName: p.last_name || '',
            role: p.role || undefined,
            country: p.country || '',
            cityId: p.city ? String(p.city.id) : '',
            timezone: p.timezone || '',
            startingPrice: p.starting_price ?? undefined,
            businessAddress: p.address?.address || '',
            shortBio: p.bio || undefined,
            website: p.social_links?.website || undefined,
            phone: p.phone_no || '',
            geoRegions: p.geo_regions || [],
            languages: p.languages || [],
            subcategories: p.subcategories?.map((subcategory) => String(subcategory.id)) || [],
            avatar: avatarItem,
            linkedin: p.social_links?.linkedin_url || undefined,
            github: p.social_links?.github_url || undefined,
            x: p.social_links?.x_url || undefined,
            fiverr: p.social_links?.fiverr_url || undefined,
            upwork: p.social_links?.upwork_url || undefined,
            toptal: p.social_links?.toptal_url || undefined,
            storetasker: p.social_links?.storetasker_url || undefined,
            otherListing: p.social_links?.other_listing_url || undefined,
            badgeIds: p.badges?.map(badge => badge.id) || [],
            partnerTier: p.partner_tier || undefined,
            appTier: undefined,
            shopifyDirectoryLink: p.social_links?.shopify_directory_link || undefined,
            bookingCallUrl: p.social_links?.booking_call_url || undefined,
            googleMerchant: p.social_links?.google_merchant || undefined,
        };
    }
};

const detailsStore = useListingDetailsStore();
const model = detailsStore.model;
const initialValues = detailsStore.initial;
const isDirty = detailsStore.isDirty;
const isValid = detailsStore.isValid;

watch(profile, (p) => {
    if (!p) return;
    initialValues.value = getInitialValues();
}, { immediate: true });

watch(hasActiveProSubscription, (value) => {
    detailsStore.hasActiveProSubscription.value = value;
}, { immediate: true });

const isAgency = computed(() => profile.value?.expert_type === 'agency');
const isAppServiceProvider = computed(() =>
    isAgency.value &&
    !!profile.value &&
    'agency_type' in profile.value &&
    profile.value.agency_type === 'app_service_provider'
);

const activeSchema = computed(() => {
    const baseSchema = isAgency.value ? agencyDetailsSchema : freelancerDetailsSchema;

    return baseSchema.superRefine((data, ctx) => {
        if ((data.subcategories?.length || 0) > subcategoryLimit.value) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `You can select up to ${subcategoryLimit.value} subcategories`,
                path: ['subcategories'],
            });
        }
    });
});

const initials = computed(() => {
    const p = profile.value;
    if (!p) return 'SE';
    if (isAgency.value && 'agency_name' in p) {
        const parts = String(p.agency_name || '').trim().split(/\s+/);
        return parts.slice(0, 2).map(s => s[0] || '').join('').toUpperCase() || 'AG';
    }
    if (!isAgency.value && 'first_name' in p) {
        const first = (p.first_name || '').toString();
        const last = (p.last_name || '').toString();
        return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || 'FL';
    }
    return 'SE';
});

const representativeInitials = computed(() => {
    if (!isAgency.value) return '';
    const p = profile.value;
    if (!p || !('representative_name' in p)) return 'RP';
    const name = p.representative_name || '';
    const parts = String(name).trim().split(/\s+/);
    return parts.slice(0, 2).map(s => s[0] || '').join('').toUpperCase() || 'RP';
});

const selectedCountry = computed(() => model.value?.country || '');
const storedCity = computed(() => {
    const p = profile.value;
    return p?.city && p.country === selectedCountry.value ? p.city : null;
});

watch(selectedCountry, (next, prev) => {
    const current = model.value;
    if (!prev || next === prev || !current?.cityId) return;
    const restoresInitial = next === initialValues.value?.country && current.cityId === initialValues.value?.cityId;
    if (!restoresInitial) current.cityId = '';
});

const locationRows = computed<BuilderRowConfig[]>(() => [
    {
        columns: [
            { type: 'combobox', name: 'country', label: 'Location', props: { placeholder: 'Enter your country', options: COUNTRIES_OPTIONS } },
            {
                type: 'custom',
                name: 'cityId',
                label: 'City',
                component: CityCombobox,
                props: { country: selectedCountry.value, initialCity: storedCity.value, placeholder: 'Search your city' },
            },
        ]
    },
    {
        columns: [
            { type: 'combobox', name: 'timezone', label: 'Timezone', props: { placeholder: 'Enter your timezone', options: TIMEZONES_OPTIONS } },
        ]
    },
]);

const rows = computed<BuilderRowConfig[]>(() => {
    const p = profile.value;
    const profilePictureUrl = p?.profile_picture || undefined;
    const representativePictureUrl = p?.expert_type === 'agency' ? p?.representative_picture || undefined : undefined;

    const baseRows: BuilderRowConfig[]= [
        {
            columns: [
                { 
                    name: 'avatar', 
                    type: 'avatar-upload', 
                    props: { 
                        initials: initials.value, 
                        initialUrl: profilePictureUrl,
                        autoUpload: true,
                        uploadFn,
                        deleteFn,
                        collection: 'expert_profile_picture',
                    } 
                },
            ]
        },
    ];

    if (isAgency.value) {
        // Agency-specific form
        baseRows.push(
            {
                columns: [
                    {
                        type: 'text',
                        name: 'agencyName',
                        label: isAppServiceProvider.value ? 'App Business Name' : 'Agency Name',
                        props: { placeholder: isAppServiceProvider.value ? 'Enter your app business name' : 'Enter your agency name' }
                    },
                    { name: 'startingPrice', label: 'Starting price (USD)', type: 'currency', props: { placeholder: '450.00' } },
                ]
            },
            ...locationRows.value,
            {
                columns: [
                    { type: 'text', name: 'businessAddress', label: 'Business address', inlineHint: "We'll use this address to place your business on the map.", props: { placeholder: 'Ex. 45 Queen St, Toronto, ON M5H 2N2, Canada' } },
                ]
            },
            {
                columns: [
                    { name: 'shortBio', label: 'Short Bio', type: 'richtext', props: { placeholder: 'Write a short bio ...' } },
                ]
            },
            {
                columns: [
                    {
                        name: 'subcategories',
                        label: 'Services',
                        type: 'multiselect-chips',
                        hint: subcategoryHint.value,
                        props: {
                            placeholder: 'Select the services you offer',
                            options: subcategoryOptions.value,
                            optionGroups: subcategoryOptionGroups.value,
                            max: subcategoryLimit.value,
                            openOnClick: true,
                            showChevron: true,
                        }
                    },
                ]
            },
            {
                columns: [
                    {
                        heading: isAppServiceProvider.value ? 'App Representative' : 'Agency Representative',
                        subtitle: isAppServiceProvider.value
                            ? 'Show leads who represents your app business and who manages your app listing.'
                            : 'Show leads who represents your agency and who manages your agency listing.',
                        divider: true
                    },
                ]
            },
            {
                columns: [
                    { 
                        name: 'representativePicture', 
                        type: 'avatar-upload', 
                        props: { 
                            initials: representativeInitials.value, 
                            initialUrl: representativePictureUrl,
                            autoUpload: true,
                            uploadFn,
                            deleteFn,
                            collection: 'agency_representative_profile',
                        } 
                    },
                ]
            },
            {
                columns: [
                    { type: 'text', name: 'representativeName', label: 'Representative Name', inlineHint: 'The full name of the responsible person', props: { placeholder: 'Enter representative full name' } },
                    {
                        name: 'representativeRole', label: 'Representative Role', type: 'select', props: {
                            placeholder: 'Select representative role ...',
                            options: agencyRepresentativeRoleOptions,
                        }
                    },
                ]
            },
            {
                columns: [
                    { name: 'website', label: 'Website', locked: true, type: 'url', props: { placeholder: 'www.example.com' } },
                    { name: 'phone', label: 'Phone number', type: 'text', locked: true, inlineHint: 'Global phone number with country code (e.g., +1234567890)', props: { placeholder: 'Enter your phone number with country code' } },
                ]
            }
        );
    } else {
        baseRows.push(
            {
                columns: [
                    { type: 'text', name: 'firstName', label: 'First Name', props: { placeholder: 'Enter your first name' } },
                    { type: 'text', name: 'lastName', label: 'Last Name', props: { placeholder: 'Enter your last name' } },
                ]
            },
            {
                columns: [
                    {
                        name: 'role', label: 'Role', type: 'select', props: {
                            placeholder: 'Select role ...',
                            options: ROLES,
                        }
                    },
                    { name: 'startingPrice', label: 'Starting price (USD)', type: 'currency', props: { placeholder: '450.00' } },
                ]
            },
            ...locationRows.value,
            {
                columns: [
                    { type: 'text', name: 'businessAddress', label: 'Business address', inlineHint: "We'll use this address to place your business on the map.", props: { placeholder: 'Ex. 45 Queen St, Toronto, ON M5H 2N2, Canada' } },
                ]
            },
            {
                columns: [
                    { name: 'shortBio', label: 'Short Bio', type: 'richtext', props: { placeholder: 'Write a short bio ...' } },
                ]
            },
            {
                columns: [
                    {
                        name: 'subcategories',
                        label: 'Services',
                        type: 'multiselect-chips',
                        hint: subcategoryHint.value,
                        props: {
                            placeholder: 'Select the services you offer',
                            options: subcategoryOptions.value,
                            optionGroups: subcategoryOptionGroups.value,
                            max: subcategoryLimit.value,
                            openOnClick: true,
                            showChevron: true,
                        }
                    },
                ]
            },
            {
                columns: [
                    { name: 'website', label: 'Website', locked: true, type: 'url', props: { placeholder: 'www.example.com' } },
                    { name: 'phone', label: 'Phone number', type: 'text', locked: true, inlineHint: 'Global phone number with country code (e.g., +1234567890)', props: { placeholder: 'Enter your phone number with country code' } },
                ]
            }
        );
    }

    // Common rows for both agency and freelancer
    baseRows.push(
        {
            columns: [
                { heading: 'Other Links' },
            ]
        },
        {
            columns: [
                {
                    type: 'url',
                    name: 'shopifyDirectoryLink',
                    label: isAppServiceProvider.value ? 'App Store Link' : 'Partner Directory Link',
                    props: {
                        placeholder: isAppServiceProvider.value
                            ? 'https://apps.shopify.com/your-app'
                            : 'https://partners.shopify.com/...'
                    }
                },
                ...(!isAppServiceProvider.value
                    ? [{
                        name: 'partnerTier',
                        label: 'Partner Tier',
                        type: 'select' as const,
                        inlineHint: "We'll verify your partner tier, so please ensure it's accurate.",
                        props: { placeholder: 'Select partner tier...', options: PARTNER_TIER_OPTIONS }
                    }]
                    : []),
            ]
        },
        ...(isAppServiceProvider.value
            ? [{
                columns: [
                    {
                        name: 'appTier',
                        label: '',
                        type: 'checkbox' as const,
                        inlineHint: 'Enable this only if your app qualifies for the Built for Shopify badge.',
                        props: { label: 'This app is Built for Shopify' }
                    },
                ]
            }]
            : []),
        {
            columns: [
                {
                    type: 'url',
                    name: 'bookingCallUrl',
                    label: 'Booking call link',
                    proBadge: true,
                    hint: 'Shows the free consultation CTA on Pro public profiles.',
                    props: { placeholder: 'https://calendly.com/your-booking-link' }
                },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'googleMerchant', label: 'Google Merchant listing', locked: true, props: { placeholder: 'Enter your Google Merchant listing URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'linkedin', label: 'Linkedin profile', locked: true, props: { placeholder: 'Enter your LinkedIn profile URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'github', label: 'Github profile', locked: true, props: { placeholder: 'Enter your Github profile URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'x', label: 'X profile', locked: true, props: { placeholder: 'Enter your X profile URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'fiverr', label: 'Fiverr profile', locked: true, props: { placeholder: 'Enter your Fiverr profile URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'upwork', label: 'Upwork profile', locked: true, props: { placeholder: 'Enter your Upwork profile URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'toptal', label: 'Toptal profile', locked: true, props: { placeholder: 'Enter your Toptal profile URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'storetasker', label: 'Storetasker profile', locked: true, props: { placeholder: 'Enter your Storetasker profile URL' } },
            ]
        },
        {
            columns: [
                { type: 'url', name: 'otherListing', label: 'Other listing', locked: true, props: { placeholder: 'Enter another marketplace listing URL' } },
            ]
        },
        {
            columns: [
                {
                    name: 'geoRegions',
                    label: 'Regions Served',
                    type: 'multiselect-chips',
                    locked: true,
                    disabled: !hasActiveProSubscription.value,
                    inlineHint: hasActiveProSubscription.value ? 'Add up to 5 regions you serve' : undefined,
                    props: {
                        placeholder: hasActiveProSubscription.value ? 'Please type and select the regions you serve' : 'Upgrade to Pro to edit regions served',
                        options: geoRegionOptions.value,
                        max: 5,
                        openOnClick: true
                    }
                },
            ]
        },
        {
            columns: [
                { name: 'languages', label: 'Languages', type: 'multiselect-chips', props: { placeholder: 'Please type and select the languages you speak', options: LANGUAGES } },
            ]
        }
    );

    return baseRows;
});

const formRef = useTemplateRef('formRef');

defineExpose({
    validate: () => {
        console.log(`[ListingEditorDetails] validate`);
        // Try to trigger validation by calling validate or submitForm
        if (formRef.value?.validate) {
            formRef.value.validate(true);
        }
    }
});

const isMobile = useMediaQuery('(max-width: 768px)');

const localBadges = ref<PartnerBadge[]>([]);

watch(badges, (newBadges) => {
  if (!newBadges.length) {
    localBadges.value = [];
    return;
  }
  
  if (model.value?.badgeIds?.length) {
    const badgeMap = new Map(newBadges.map(badge => [badge.id, badge]));
    localBadges.value = model.value.badgeIds
      .map(id => badgeMap.get(id))
      .filter((badge): badge is PartnerBadge => badge !== undefined);
  } else {
    localBadges.value = [...newBadges];
  }
}, { immediate: true });

function onBadgesReorder(dropResult: { removedIndex: number | null; addedIndex: number | null }) {
  if (dropResult.removedIndex === null || dropResult.addedIndex === null) return;
  
  console.log('[onBadgesReorder] Drag from', dropResult.removedIndex, 'to', dropResult.addedIndex);
  console.log('[onBadgesReorder] Before:', localBadges.value.map(b => b.name));
  
  const badges = [...localBadges.value];
  const [removed] = badges.splice(dropResult.removedIndex, 1);
  if (removed === undefined) return;
  
  badges.splice(dropResult.addedIndex, 0, removed);
  localBadges.value = badges;
  
  console.log('[onBadgesReorder] After:', localBadges.value.map(b => b.name));
  
  if (model.value) {
    model.value.badgeIds = badges.map(b => b.id);
  }
}
</script>

<template>
    <ListingEditorPage heading="Personal & Business Details" :locked="props.locked" @action-click="onActionClick">
        <template #content>
            <component :is="isMobile ? 'div' : UiCard" padding="md">
                <UiText as="h6" v-if="!profile && isLoadingProfile" text="Loading profile..." />
                <UiFormBuilder ref="formRef" v-if="profile" :schema="activeSchema" :rows="rows" scroll-to-first-error :initialValues="initialValues"
                    v-model:dirty="isDirty"
                    v-model:valid="isValid" v-model="model">
                    <template #debug="{ meta, errors, values }" v-if="DEBUG_MODE === 'true'">
                        <div class="mt-4">
                            <h3 class="text-sm font-medium">Debug</h3>

                            <div class="block p-4 bg-gray-100 rounded-lg">
                                <pre>{{ 
                                {  
                                form: {
                                    dirty: isDirty,
                                    valid: isValid,
                                    model: model,
                                    meta: meta,
                                    errors: errors,
                                    values: values,
                                }
                            }
                                 }}</pre>
                                this.profile:
                                <pre>{{ profile }}</pre><br>
                            </div>
                        </div>
                    </template>
                </UiFormBuilder>
                
                <div v-if="profile && !isAppServiceProvider" class="space-y-4 mt-8 pt-8 border-t">
                    <div class="">
                        <UiText as="h6" text="App and Theme Skills" class="font-semibold" />
                        <UiText as="p" variant="p-small" text="You can drag and drop the badges to reorder them. Contact admin to get badges assigned or removed." class="text-gray-600 mt-1" />
                    </div>
                    
                    <PartnerBadgeList
                        :badges="localBadges"
                        :draggable="true"
                        orientation="horizontal"
                        empty-text="No apps or themes added yet. Contact admin to get badges assigned."
                        @reorder="onBadgesReorder"
                    />
                </div>
            </component>
        </template>
    </ListingEditorPage>
</template>
